import {
  Box,
  Card,
  Flex,
  Icon,
  Text,
  Image,
  Spacer,
  LinkBox,
  CardBody,
  CardProps,
  LinkOverlay
} from '@homebotapp/hb-react-component-catalog'
import history from '../../../history'
import { useEffect, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import { useAppSelector } from '../../../store/hooks'
import { SEARCH_PATH_HELPERS } from '../../../constants/navigation'
import PriceDropsImg from '../../../assets/homes/img/gallery-promo-price-drops-default.png'
import RecentlySoldImg from '../../../assets/homes/img/gallery-promo-recently-sold-default.png'
import CuratedGalleriesImg from '../../../assets/homes/img/gallery-promo-general-gallery-default.jpg'

const PRICE_DROP_NUM = 1
const RECENTLY_SOLD_NUM = 2
const CURATED_COLLECTIONS_NUM = 3

export const MSG = defineMessages({
  priceDropAlt: {
    id: 'SidebarAd.priceDropAlt',
    defaultMessage: 'Listings with price drops'
  },
  priceDropDesc: {
    id: 'SidebarAd.priceDropDesc',
    defaultMessage: 'Get your Price Drops here! Find discounted homes.'
  },
  priceDropHead: {
    id: 'SidebarAd.priceDropHead',
    defaultMessage: 'Price Drops'
  },
  recentlySoldAlt: {
    id: 'SidebarAd.recentlySoldAlt',
    defaultMessage: 'Listings that were recently sold'
  },
  recentlySoldDesc: {
    id: 'SidebarAd.recentlySoldDesc',
    defaultMessage: 'Compare your house to sold homes in your area'
  },
  recentlySoldHead: {
    id: 'SidebarAd.recentlySoldHead',
    defaultMessage: 'Recently Sold'
  },
  curatedCollectionsAlt: {
    id: 'SidebarAd.curatedCollectionsAlt',
    defaultMessage: 'Collections of homes made just for you'
  },
  curatedCollectionsDesc: {
    id: 'SidebarAd.curatedCollectionsDesc',
    defaultMessage: 'View collections of homes made just for you'
  },
  curatedCollectionsHead: {
    id: 'SidebarAd.curatedCollectionsHead',
    defaultMessage: 'Curated Galleries'
  }
})

export interface SidebarAdProps extends CardProps {
  tracking: {
    guid: string
    ui_context: string
  }
}

export const SidebarAd = ({ tracking, ...cardProps }) => {
  const intl = useIntl()
  const [randomNumBetween1And3, setRandomNumBetween1And3] = useState(0)
  const customerProfileId = useAppSelector(state => state.customerProfile.data.id)
  const clientId = useAppSelector(state => state.client.data.id)

  useEffect(() => {
    setRandomNumBetween1And3(Math.floor(Math.random() * 3) + 1)
  }, [])

  const handleClick = e => {
    e.preventDefault()
    history.push(SEARCH_PATH_HELPERS.forYouTab.buildPath(customerProfileId, clientId))
  }

  const getAltText = () => {
    switch (randomNumBetween1And3) {
      case PRICE_DROP_NUM:
        return intl.formatMessage(MSG.priceDropAlt)
      case RECENTLY_SOLD_NUM:
        return intl.formatMessage(MSG.recentlySoldAlt)
      case CURATED_COLLECTIONS_NUM:
        return intl.formatMessage(MSG.curatedCollectionsAlt)
      default:
        return intl.formatMessage(MSG.curatedCollectionsAlt)
    }
  }

  const getHeadline = () => {
    switch (randomNumBetween1And3) {
      case PRICE_DROP_NUM:
        return intl.formatMessage(MSG.priceDropHead)
      case RECENTLY_SOLD_NUM:
        return intl.formatMessage(MSG.recentlySoldHead)
      case CURATED_COLLECTIONS_NUM:
        return intl.formatMessage(MSG.curatedCollectionsHead)
      default:
        return intl.formatMessage(MSG.curatedCollectionsHead)
    }
  }

  const getDescription = () => {
    switch (randomNumBetween1And3) {
      case PRICE_DROP_NUM:
        return intl.formatMessage(MSG.priceDropDesc)
      case RECENTLY_SOLD_NUM:
        return intl.formatMessage(MSG.recentlySoldDesc)
      case CURATED_COLLECTIONS_NUM:
        return intl.formatMessage(MSG.curatedCollectionsDesc)
      default:
        return intl.formatMessage(MSG.curatedCollectionsDesc)
    }
  }

  const getImage = () => {
    switch (randomNumBetween1And3) {
      case PRICE_DROP_NUM:
        return PriceDropsImg
      case RECENTLY_SOLD_NUM:
        return RecentlySoldImg
      case CURATED_COLLECTIONS_NUM:
        return CuratedGalleriesImg
      default:
        return CuratedGalleriesImg
    }
  }

  const getTrackingDescriptor = () => {
    switch (randomNumBetween1And3) {
      case PRICE_DROP_NUM:
        return 'Clicked on Price Drops'
      case RECENTLY_SOLD_NUM:
        return 'Clicked on Recently Sold'
      case CURATED_COLLECTIONS_NUM:
        return 'Clicked on Curated Galleries'
      default:
        return 'Clicked on Curated Galleries'
    }
  }

  return (
    <LinkBox>
      <Card
        {...cardProps}
        onClick={handleClick}
        size='sm'
        role='button'
        overflow='hidden'
        _focusWithin={{
          boxShadow: 'outline'
        }}
      >
        <Box aspectRatio={16 / 9} overflow='hidden'>
          <Image src={getImage()} alt={getAltText()} transform='translateY(-20%)' />
        </Box>
        <CardBody>
          <LinkOverlay href='#' onClick={handleClick} tracking={{ ...tracking, descriptor: getTrackingDescriptor() }}>
            <Flex direction='row' alignItems='center' marginBottom='1'>
              <Text
                size='3xs'
                marginBottom='0'
                textTransform='uppercase'
                color={`${randomNumBetween1And3 === PRICE_DROP_NUM ? 'success' : 'primary'}.700`}
              >
                {getHeadline()}
              </Text>
              <Spacer />
              <Icon
                width='3'
                height='3'
                name='circle-arrow-right-bts'
                color={`${randomNumBetween1And3 === PRICE_DROP_NUM ? 'success' : 'primary'}.700`}
              />
            </Flex>
          </LinkOverlay>
          <Text color='neutral.900' size='2xs' fontWeight='semibold' mb='0'>
            {getDescription()}
          </Text>
        </CardBody>
      </Card>
    </LinkBox>
  )
}
